import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { type ErrorComponentProps, useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { messageOf, STOP_SCOPES, type Stop, statusOf, stopFor } from '@/api/stops'
import { useT } from '@/session/useSession'
import { Loading } from '../Loading/Loading'
import { NavLink } from '../NavLink/NavLink'
import { Panel } from '../Panel/Panel'
import { Button } from '../ui/button'

export type Home = { to: string; labelKey: string }

export function StatusPage({
  stop,
  home,
  detail,
  actions,
}: {
  stop: Stop
  home: Home
  detail?: string
  actions?: ReactNode
}) {
  const t = useT()
  const scope = STOP_SCOPES[stop]

  return (
    <Panel level={1} title={t(`${scope}.heading`)} subtitle={t(`${scope}.body`)}>
      {detail ? <p>{detail}</p> : null}
      <NavLink to={home.to}>{t(home.labelKey)}</NavLink>
      {actions}
    </Panel>
  )
}

export function statusPagesFor(home: Home) {
  function ErrorPage({ error, reset }: ErrorComponentProps) {
    const t = useT()
    const router = useRouter()
    const queries = useQueryErrorResetBoundary()
    const status = statusOf(error)

    return (
      <StatusPage
        stop={stopFor(status)}
        home={home}
        detail={status === 429 ? messageOf(t, status) : undefined}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              queries.reset()
              reset()
              router.invalidate()
            }}
          >
            {t('actions.retry')}
          </Button>
        }
      />
    )
  }

  const NotFoundPage = () => <StatusPage stop="not_found" home={home} />

  return { ErrorPage, NotFoundPage }
}

export function PendingPage() {
  const t = useT()

  return <Loading label={t('pages.loading')} />
}

export function StatusPanel({
  title,
  error,
  onRetry,
}: {
  title: string
  error: unknown
  onRetry: () => void
}) {
  const t = useT()

  return (
    <Panel title={title} subtitle={messageOf(t, statusOf(error))}>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('actions.retry')}
      </Button>
    </Panel>
  )
}
